import React, {useState, useEffect} from "react";
import moment from "moment";
import {addressAndRatingDetails, deleteAddressAndRating, editAddressAndRating, likeService, upliftService} from "../../services/rating";
import CustomLoader from "../../CustomComponents/CustomLoader";
import {sweetAlertQuestion, sweetAlerts} from "../../utils/sweet-alerts";
import {issueLabels, jobIssuesOptions} from "../../constant";
import {useLocation} from "react-router-dom";
import {fDate, fDateTime} from "../../utils/format-time";
import CustomRatingStar from "../../CustomComponents/CustomRatingStar";
import Avatar from "@mui/material/Avatar";
import CustomUserName from "../../CustomComponents/CustomUserName";

const MyRatings = () => {
	const location = useLocation();

	const [firstTimeLoad, setFirstTimeLoad] = useState(true);
	const [loading, setLoading] = useState(true);
	const [loadingButtons, setLoadingButtons] = useState({});

	const [ratings, setRatings] = useState([]);
	const [mockRatings, setMockRatings] = useState([]);
	const [filteredRatings, setFilteredRatings] = useState([]);

	const [filterStatus, setFilterStatus] = useState(location.state?.filter || "all");
	const [filterRating, setFilterRating] = useState("all");
	const [sortBy, setSortBy] = useState("date-desc");

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalLength, setTotalLength] = useState(0);

	const [selectedRating, setSelectedRating] = useState(null);
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

	const [showEditModal, setShowEditModal] = useState(false);
	const [editingRating, setEditingRating] = useState(null);
	const [editFormData, setEditFormData] = useState({});
	const [editErrors, setEditErrors] = useState({});

	// const indexOfLastRating = currentPage * pageSize;
	// const indexOfFirstRating = indexOfLastRating - pageSize;
	// const currentRatings = filteredRatings.slice(indexOfFirstRating, indexOfLastRating);
	// const totalPages = Math.ceil(filteredRatings.length / pageSize);

	const paginate = (pageNumber) => {
		setPage(pageNumber);
	};

	const getMockRatings = async () => {
		const dummyRatings = [];
		try {
			if (firstTimeLoad) {
				setLoading(false);
			}

			const response = await addressAndRatingDetails({
				pagesize: pageSize,
				page: page,
				rating: filterRating,
				status: filterStatus,
				sortby: sortBy,
			});

			if (response && response.status === true && Array.isArray(response?.data?.list) && response?.data?.list?.length > 0) {
				const transformed = response?.data?.list?.map((item) => {
					const jobDate = item.job_date ? moment(item.job_date) : null;
					const submittedDate = item.createdAt ? moment(item.createdAt) : null;
					const likes = item?.likes || null;
					const trueCount = likes.filter((item) => item?.like == true).length;
					return {
						id: item.rating_id,
						jobAddress: item.address?.full_address || "N/A",
						jobRating: item.stars,
						jobDate: jobDate ? jobDate.format("YYYY-MM-DD") : "",
						jobIssues: typeof item?.job_issues === "string" ? JSON.parse(item?.job_issues) : item?.job_issues || [],
						parent_uplift_id: item?.parent_uplift_id,
						status: item.is_historic ? "historic" : "fresh",
						submittedDate: submittedDate ? submittedDate.format("YYYY-MM-DD") : "",
						canEdit: !item.is_historic && !(item?.deleted || false),
						daysAgo: jobDate ? moment().diff(jobDate, "days") : 0,
						flagged: item.admin_flagged || false,
						likeCount: trueCount,
						selfLike: item?.selfLike,
						upliftCount: item?.upliftCount,
						upliftByUser: item?.upliftByUser,
						selfCreate: item?.selfCreate,
						activity_log: item?.activity_log,
						uplift: item?.uplift,
						likes: item?.likes,
						upliftList: item?.upliftList,
						deleted: item?.deleted || false,
					};
				});

				setMockRatings(transformed);

				setTotalLength(response?.data?.totalCount);
				const totalPages = Math.ceil(response?.data?.totalCount / pageSize);
				setTotalPages(totalPages);
			} else {
				setTotalLength(0);
				setTotalPages(0);
				setMockRatings(dummyRatings);
			}
			setLoading(true);
		} catch (error) {
			setLoading(true);
			setTotalLength(0);
			setTotalPages(0);
			setMockRatings(dummyRatings);
		}
	};

	useEffect(() => {
		getMockRatings();
	}, [pageSize, page]);

	useEffect(() => {
		if (firstTimeLoad) {
			setFirstTimeLoad(false);
		} else {
			setPage(1);
			getMockRatings();
		}
	}, [filterStatus, filterRating, sortBy]);

	useEffect(() => {
		setRatings(mockRatings);
		// setFilteredRatings(mockRatings);
		setLoadingButtons({});
	}, [mockRatings]);

	const getRatingColor = (rating) => {
		if (rating >= 4) return "rating-excellent";
		if (rating >= 3) return "rating-good";
		if (rating >= 2) return "rating-fair";
		return "rating-poor";
	};

	const renderStars = (rating) => {
		return [...Array(5)].map((_, index) => (
			<span key={index} className={`star ${index < rating ? "filled" : ""}`}>
				★
			</span>
		));
	};

	const getStatusBadge = (status, flagged) => {
		if (flagged) {
			return <span className="badge badge-danger">Flagged</span>;
		}

		switch (status) {
			case "fresh":
				return <span className="badge badge-success">Fresh</span>;
			case "historic":
				return <span className="badge badge-warning">Historic</span>;
			default:
				return <span className="badge badge-secondary">Unknown</span>;
		}
	};

	const handleViewDetails = (rating) => {
		setSelectedRating(rating);
		setShowDetailModal(true);
	};

	const openEditModal = (rating) => {
		if (!rating.canEdit) {
			if (rating.deleted) {
				sweetAlerts("error", "This rating cannot be edited because it has been removed by an admin.");
			} else {
				sweetAlerts("error", "This rating cannot be edited because it's older than 90 days or has been flagged.");
			}
			return;
		}
		setEditingRating(rating);
		setEditFormData({
			id: rating.id,
			jobAddress: rating.jobAddress,
			jobRating: rating.jobRating,
			jobDate: rating.jobDate,
			jobIssues: typeof rating.jobIssues === "string" ? JSON.parse(rating.jobIssues) : rating.jobIssues,
			canEdit: rating.status === "historic" ? false : !rating.deleted,
		});
		setEditErrors({});
		setShowEditModal(true);
	};

	const handleEditRating = (ratingId) => {
		const rating = ratings.find((r) => r.id === ratingId);
		if (rating) {
			openEditModal(rating);
		}
	};

	const handleDeleteRating = async (ratingId) => {
		sweetAlertQuestion()
			.then(async (result) => {
				if (result === "Yes") {
					setLoadingButtons((prev) => ({...prev, [`delete-${ratingId}`]: true}));

					try {
						const response = await deleteAddressAndRating(ratingId);

						if (response && response.status === true) {
							if (mockRatings.length === 1) {
								setPage(page - 1);
							}
							await getMockRatings();
							sweetAlerts("success", "Rating deleted successfully");
						} else {
							sweetAlerts("error", "Failed deleting rating. Please try again.");
						}
					} catch (error) {
						sweetAlerts("error", "Error deleting rating. Please try again.");
					} finally {
						setLoadingButtons((prev) => ({...prev, [`delete-${ratingId}`]: false}));
					}
				}
			})
			.catch((error) => {
				console.error(error);
			});
	};

	const handleLike = async (rating_id) => {
		setLoadingButtons((prev) => ({...prev, [`like-${rating_id}`]: true}));
		try {
			const response = await likeService({rating_id: rating_id});

			if (response.data.status) {
				await getMockRatings();
			}
		} catch (error) {
			console.error("Error fetching ratings:", error);
		} finally {
		}
	};

	const handleUplift = async (rating_id) => {
		setLoadingButtons((prev) => ({...prev, [`uplift-${rating_id}`]: true}));
		try {
			const response = await upliftService({rating_id: rating_id});

			if (response.data.status) {
				await getMockRatings();
			}
		} catch (error) {
			console.error("Error fetching ratings:", error);
		} finally {
		}
	};

	const closeEditModal = () => {
		setShowEditModal(false);
		setEditingRating(null);
		setEditFormData({});
		setEditErrors({});
		// Clear loading state for edit button
		if (editingRating) {
			setLoadingButtons((prev) => ({...prev, [`edit-${editingRating.id}`]: false}));
		}
	};

	const handleEditInputChange = (field, value) => {
		setEditFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Clear errors when user starts typing
		if (editErrors[field]) {
			setEditErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}
	};

	const handleEditIssueToggle = (issueId) => {
		setEditFormData((prev) => ({
			...prev,
			jobIssues: prev.jobIssues.includes(issueId) ? prev.jobIssues.filter((id) => id !== issueId) : [...prev.jobIssues, issueId],
		}));
	};

	const handleEditRatingChange = (rating) => {
		setEditFormData((prev) => ({
			...prev,
			jobRating: rating,
		}));

		if (editErrors.jobRating) {
			setEditErrors((prev) => ({
				...prev,
				jobRating: null,
			}));
		}
	};

	const calculateJobDateStatus = (jobDate) => {
		const job = new Date(jobDate);
		const today = new Date();
		const diffInDays = Math.floor((today - job) / (1000 * 60 * 60 * 24));

		if (diffInDays <= 90) {
			return {
				type: "fresh",
				days: diffInDays,
				label: "Fresh Rating",
				description: `Job completed ${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`,
			};
		} else {
			return {
				type: "historic",
				days: diffInDays,
				label: "Previous History",
				description: `Job completed ${diffInDays} days ago (flagged as historic)`,
			};
		}
	};

	const getChanges = () => {
		const changes = [];
		const original = editingRating;
		const edited = editFormData;

		if (original.jobAddress !== edited.jobAddress) {
			changes.push({
				field: "Job Address",
				from: original.jobAddress,
				to: edited.jobAddress,
			});
		}

		if (original.jobRating !== edited.jobRating) {
			changes.push({
				field: "Rating",
				from: `${original.jobRating} star${original.jobRating !== 1 ? "s" : ""}`,
				to: `${edited.jobRating} star${edited.jobRating !== 1 ? "s" : ""}`,
			});
		}

		if (original.jobDate !== edited.jobDate) {
			changes.push({
				field: "Job Date",
				from: new Date(original.jobDate).toLocaleDateString(),
				to: new Date(edited.jobDate).toLocaleDateString(),
			});
		}

		const originalIssues = typeof original.jobIssues === "string" ? JSON.parse(original.jobIssues) : original.jobIssues;
		const editedIssues = edited.jobIssues;

		if (JSON.stringify(originalIssues.sort()) !== JSON.stringify(editedIssues.sort())) {
			changes.push({
				field: "Job Issues",
				from: originalIssues.length > 0 ? originalIssues.map((id) => jobIssuesOptions.find((option) => option.id === id)?.label || id).join(", ") : "None",
				to: editedIssues.length > 0 ? editedIssues.map((id) => jobIssuesOptions.find((option) => option.id === id)?.label || id).join(", ") : "None",
			});
		}

		return changes;
	};

	const handleEditSubmit = async () => {
		if (!validateEditForm()) {
			return;
		}

		const changes = getChanges();
		if (changes.length === 0) {
			sweetAlerts("error", "No changes detected.");
			return;
		}

		const changesList = changes.map((change) => `• ${change.field}: "${change.from}" → "${change.to}"`).join("\n");

		// sweetAlertQuestion(`Are you sure you want to save these changes?\n\n${changesList}\n\nNote: This edit will be tracked for quality assurance purposes.`).then(async (result) => {
		sweetAlertQuestion(`You will lose all reactions when editing a rating.`).then(async (result) => {
			if (result === "Yes") {
				try {
					setIsSubmittingEdit(true);
					setLoadingButtons((prev) => ({...prev, [`edit-${editingRating.id}`]: true}));
					await new Promise((resolve) => setTimeout(resolve, 2000));

					const updatedRating = {
						...editingRating,
						...editFormData,
						lastEdited: new Date().toISOString(),
						editCount: (editingRating.editCount || 0) + 1,
						changes: [
							...(editingRating.changes || []),
							{
								date: new Date().toISOString(),
								changes: changes,
								editNumber: (editingRating.editCount || 0) + 1,
							},
						],
					};
					setRatings((prev) => prev.map((rating) => (rating.id === editingRating.id ? updatedRating : rating)));
					const response = await editAddressAndRating({
						...editFormData,
					});

					if (response && response.status === true) {
						sweetAlerts("success", "Rating updated successfully! Your changes have been saved and tracked.");
						await getMockRatings();
					} else {
						sweetAlerts("error", response?.message || "Failed updating rating. Please try again.");
					}
					closeEditModal();
				} catch (error) {
					sweetAlerts("error", error?.message || "Error updating rating. Please try again.");
				} finally {
					setIsSubmittingEdit(false);
				}
			} else {
				setLoadingButtons((prev) => ({...prev, [`edit-${editingRating.id}`]: false}));
				return;
			}
		});
	};

	const validateEditForm = () => {
		const errors = {};

		if (!editFormData.jobAddress?.trim()) {
			errors.jobAddress = "Job address is required";
		}

		if (!editFormData.jobRating || editFormData.jobRating < 1 || editFormData.jobRating > 5) {
			errors.jobRating = "Please select a rating between 1 and 5 stars";
		}

		if (!editFormData.jobDate) {
			errors.jobDate = "Job date is required";
		} else {
			const jobDate = new Date(editFormData.jobDate);
			const today = new Date();
			if (jobDate > today) {
				errors.jobDate = "Job date cannot be in the future";
			}
		}

		setEditErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const ObjectString = ({recode_data, label, createdAt}) => {
		let changesData = recode_data;

		if (typeof recode_data === "string") {
			try {
				changesData = JSON.parse(recode_data);
			} catch (err) {
				changesData = {};
			}
		}

		const excludeKeys = [
			"sent_by",
			"user_id",
			"recode_id",
			"notification_id",
			"createdAt",
			"updatedAt",
			"recode_data",
			"old_recode_data",
			"deleted",
			"is_read",
			"password",
			"is_suspended",
			"daily_ratings",
			"is_contractor",
			"is_google_verified",
			"auth_otp",
			"admin_edited",
			"admin_blocked",
			"client_name",
			"is_active",
			"is_historic",
		];

		const labelMap = {
			stars: "Rating",
			deleted: "Deleted",
			total_ratings: "Total Ratings",
			active_ratings: "Active Ratings",
			aggregate_rating: "Aggregate Rating",
			is_historic: "Is Historic",
			issue_summary: "Issue Summary",
			type: "Type",
			table: "Table",
			title: "Title",
		};

		const isDateValue = (value) => {
			if (!value) return false;
			if (typeof value !== "string") return false;
			return /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) || /\d{4}-\d{2}-\d{2}/.test(value);
		};

		// ✅ Format value based on type and key
		const formatValue = (key, value) => {
			if (value === null || value === undefined) return "N/A";
			if (typeof value === "boolean") return value ? "Yes" : "No";
			if (isDateValue(value)) return fDateTime(value);
			if (key === "stars" || key === "rating") return <CustomRatingStar star={Number(value)} />; // ⭐️ Rating stars
			if (typeof value === "object") return JSON.stringify(value);
			return String(value);
		};

		const renderChangeItem = (key, changeData) => {
			if (excludeKeys.includes(key)) return null;
			const readableLabel = labelMap[key] || key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

			if (changeData && typeof changeData === "object" && changeData.hasOwnProperty("to") && changeData.hasOwnProperty("from")) {
				return (
					<div className="change-item" key={key}>
						<div className="change-label">
							<strong>{readableLabel}:</strong>
						</div>
						<div className="change-values  ">
							<div className="change-from ">
								<span className="change-label-text">From:</span>
								<span className="change-value from-value">{formatValue(key, changeData.from)}</span>
							</div>
							<div className="change-to ">
								<span className="change-label-text">To:</span>
								<span className="change-value to-value">{formatValue(key, changeData.to)}</span>
							</div>
						</div>
					</div>
				);
			}
		};

		return (
			<div className="changes-container">
				<h6 className="changes-title">
					{label} : {createdAt}
				</h6>
				<div className="changes-list">
					{Object.entries(changesData)
						.map(([key, value]) => renderChangeItem(key, value))
						.filter((item) => item !== null)}
				</div>
			</div>
		);
	};

	const formatDateTime = (dateString) => {
		return new Date(dateString).toLocaleString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: true,
		});
	};

	return (
		<div className="dashboard-page">
			<div className="page-header">
				<h2>My Ratings</h2>
				<p>View and manage all your submitted ratings ({totalLength} total)</p>
			</div>

			<div className="ratings-controls">
				<div className="filter-section">
					<select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
						<option value="all">All Status</option>
						<option value="fresh">Fresh Ratings</option>
						<option value="historic">Historic Ratings</option>
						<option value="flagged">Flagged Ratings</option>
					</select>

					<select className="filter-select" value={filterRating} onChange={(e) => setFilterRating(e.target.value)}>
						<option value="all">All Ratings</option>
						<option value="5">5 Stars</option>
						<option value="4">4 Stars</option>
						<option value="3">3 Stars</option>
						<option value="2">2 Stars</option>
						<option value="1">1 Star</option>
					</select>

					<select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
						<option value="date-desc">Newest First</option>
						<option value="date-asc">Oldest First</option>
						<option value="rating-desc">Highest Rating</option>
						<option value="rating-asc">Lowest Rating</option>
						{/* <option value="address">Address A-Z</option> */}
					</select>
				</div>
			</div>

			{loading ? (
				<div className="ratings-container">
					{mockRatings.length === 0 ? (
						<div className="no-ratings">
							<div className="no-ratings-icon">
								<svg width="64" height="64" viewBox="0 0 24 24" fill="none">
									<path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" />
								</svg>
							</div>
							<h3>No ratings found</h3>
							<p>Try adjusting your search or filter criteria</p>
						</div>
					) : (
						<div className="ratings-grid">
							{mockRatings.map((rating) => {
								const isRemoved = rating.deleted;
								const removedStyle = isRemoved ? {opacity: 0.4, filter: "grayscale(0.6)"} : {};

								return (
									<div key={rating.id} className={`rating-card ${rating.flagged ? "flagged" : ""}`} style={removedStyle}>
									{rating.flagged && (
										<div className="m-4 mb-0 text-danger" style={{fontSize: "12px"}}>
											This rating has been flagged by the admin. Please contact the admin for clarification.
										</div>
									)}

									<div className="rating-card-header cursor-pointer" onClick={() => handleViewDetails(rating)}>
										<div className="address-info">
											<h4 className="job-address">{rating.jobAddress}</h4>
											<div className="rating-info">
												<div className={`rating-stars ${getRatingColor(rating.jobRating)}`}>
													{renderStars(rating.jobRating)}
													<span className="rating-number">({rating.jobRating}/5)</span>
												</div>
												{getStatusBadge(rating.status, rating.flagged)}
											</div>
										</div>

										<div className="card-actions">
											<button className="btn-icon" onClick={() => handleViewDetails(rating)} title="View Details">
												<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
													<path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" />
													<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
												</svg>
											</button>

											{rating.canEdit && !isRemoved && (
												<>
													<button
														className="btn-icon btn-edit"
														onClick={(e) => {
															e.stopPropagation();
															handleEditRating(rating.id);
														}}
														title="Edit Rating"
														disabled={loadingButtons[`edit-${rating.id}`]}>
														{loadingButtons[`edit-${rating.id}`] ? (
															<span className="spinner-small"></span>
														) : (
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path
																	d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
																	stroke="currentColor"
																	strokeWidth="2"
																	strokeLinecap="round"
																	strokeLinejoin="round"
																/>
																<path
																	d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
																	stroke="currentColor"
																	strokeWidth="2"
																	strokeLinecap="round"
																	strokeLinejoin="round"
																/>
															</svg>
														)}
													</button>

													{/* <button
														className="btn-icon btn-delete"
														onClick={(e) => {
															e.stopPropagation();
															handleDeleteRating(rating.id);
														}}
														title="Delete Rating"
														disabled={loadingButtons[`delete-${rating.id}`]}>
														{loadingButtons[`delete-${rating.id}`] ? (
															<span className="spinner-small"></span>
														) : (
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																<path
																	d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
																	stroke="currentColor"
																	strokeWidth="2"
																	strokeLinecap="round"
																	strokeLinejoin="round"
																/>
															</svg>
														)}
													</button> */}
												</>
											)}
										</div>
									</div>

									<div className="rating-card-body cursor-pointer" onClick={() => handleViewDetails(rating)}>
										<div className="job-details">
											<div className="detail-item">
												<span className="detail-label">Job Date:</span>
												<span className="detail-value">{moment(rating.jobDate).format("MMM DD, YYYY")}</span>
												<span className="days-ago">({rating.daysAgo} days ago)</span>
											</div>

											{Array.isArray(rating?.jobIssues) && rating.jobIssues.length > 0 && (
												<div className="detail-item">
													<span className="detail-label">Issues:</span>
													<div className="issues-list">
														{rating.jobIssues.slice(0, 2).map((issue) => (
															<span key={issue} className="issue-tag">
																{issueLabels[issue]}
															</span>
														))}
														{rating.jobIssues.length > 2 && <span className="issue-tag more">+{rating.jobIssues.length - 2} more</span>}
													</div>
												</div>
											)}
										</div>
									</div>

									<div className="rating-card-footer">
										<div className="footer-left">
											<span className="submitted-date">Submitted: {moment(rating.submittedDate).format("MMM DD, YYYY")}</span>
										</div>

										<div className="footer-right">
											<div className="interaction-buttons">
												<button className={`interaction-btn  disabled like-btn ${rating?.selfLike?.like ? "active" : ""}`} title="Like this rating" disabled={isRemoved || loadingButtons[`like-${rating.id}`]}>
													{loadingButtons[`like-${rating.id}`] ? (
														<span className="spinner-small"></span>
													) : (
														<div className={rating?.selfLike?.like ? "bi-hand-thumbs-up-fill" : "bi-hand-thumbs-up"} style={{fontSize: 14}} />
													)}
													<span>{loadingButtons[`like-${rating.id}`] ? "Liking..." : "Like"}</span>
													<span className="interaction-count">({rating?.likeCount || 0})</span>
												</button>

												<button
													className={`interaction-btn  uplift-btn ${rating?.uplift ? "active" : "disabled"}`}
													onClick={() => {
														if (rating?.parent_uplift_id) {
															handleUplift(rating.id);
														}
													}}
													title="Uplift this rating"
													disabled={isRemoved || loadingButtons[`uplift-${rating.id}`]}>
													{loadingButtons[`uplift-${rating.id}`] ? (
														<span className="spinner-small"></span>
													) : (
														<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
															<path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
														</svg>
													)}
													<span>{loadingButtons[`uplift-${rating.id}`] ? "Uplifting..." : "Uplift"}</span>
													{rating?.upliftCount > 0 && <span className="interaction-count">({rating?.upliftCount || 0})</span>}
												</button>
											</div>
										</div>
									</div>
								</div>
								);
							})}
						</div>
					)}

					{totalPages > 1 && (
						<div className="pagination">
							<button className="pagination-btn" onClick={() => paginate(page - 1)} disabled={page === 1}>
								Previous
							</button>

							<div className="pagination-numbers">
								{[...Array(totalPages)].map((_, index) => {
									const ipage = index + 1;
									if (ipage === 1 || ipage === totalPages || (ipage >= page - 1 && ipage <= page + 1)) {
										return (
											<button key={ipage} className={`pagination-number ${page === ipage ? "active" : ""}`} onClick={() => paginate(ipage)}>
												{ipage}
											</button>
										);
									} else if (ipage === page - 2 || ipage === page + 2) {
										return (
											<span key={ipage} className="pagination-ellipsis">
												...
											</span>
										);
									}
									return null;
								})}
							</div>

							<button className="pagination-btn" onClick={() => paginate(page + 1)} disabled={page === totalPages}>
								Next
							</button>
						</div>
					)}
				</div>
			) : (
				<div className="ratings-container">
					<CustomLoader />
				</div>
			)}

			{showDetailModal && selectedRating && (
				<div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h3>Rating Details</h3>
							<button className="modal-close" onClick={() => setShowDetailModal(false)}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="detail-section">
								<h4>Job Information</h4>
								<div className="detail-grid">
									<div className="detail-item">
										<span className="detail-label">Address:</span>
										<span className="detail-value">{selectedRating.jobAddress}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Job Date:</span>
										<span className="detail-value">
											{moment(selectedRating.jobDate).format("MMM DD, YYYY")}
											<span className="days-ago"> ({selectedRating.daysAgo} days ago)</span>
										</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Rating:</span>
										<div className="detail-rating">
											<div className={`rating-stars ${getRatingColor(selectedRating.jobRating)}`}>
												{renderStars(selectedRating.jobRating)}
												<span className="rating-number">({selectedRating.jobRating}/5)</span>
											</div>
										</div>
									</div>
									<div className="detail-item">
										<span className="detail-label">Status:</span>
										<span className="detail-value">{getStatusBadge(selectedRating.status, selectedRating.flagged)}</span>
									</div>
								</div>
							</div>

							{Array.isArray(selectedRating?.jobIssues) && selectedRating.jobIssues.length > 0 && (
								<div className="detail-section">
									<h4>Issues Reported</h4>
									<div className="issues-list-modal">
										{selectedRating.jobIssues.map((issue) => (
											<span key={issue} className="issue-tag-modal">
												{issueLabels[issue]}
											</span>
										))}
									</div>
								</div>
							)}

							<div className="detail-section">
								<h4>Submission Information</h4>
								<div className="detail-grid">
									<div className="detail-item">
										<span className="detail-label">Submitted:</span>
										<span className="detail-value">{moment(selectedRating.submittedDate).format("MMM DD, YYYY")}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Can Edit:</span>
										<span className="detail-value">{selectedRating.canEdit ? "Yes" : "No"}</span>
									</div>
								</div>
							</div>
							{/* <div className="detail-section align-items-center ">
								<h4>Activity</h4>
								<div className="">
									{(() => {
										const actionCount = {};
										return selectedRating?.activity_log?.map((log) => {
											const action = log.action;
											actionCount[action] = (actionCount[action] || 0) + 1;

											let label = "";
											if (action === "CREATE") {
												label = "Submit date";
											} else if (action === "UPDATE") {
												label = actionCount[action] === 1 ? "Update date" : "Again update date";
											}

											return (
												<div key={log.log_id} className="text-muted small mb-1">
													{log.changes && <div className="notification-details">{ObjectString({recode_data: log.changes, label, createdAt: fDateTime(log.createdAt)})}</div>}
												</div>
											);
										});
									})()}
								</div>
							</div> */}

							<div className="detail-section">
								<h4>Activity</h4>
								<div className="text-end">
									<div className="">
										{(() => {
											const actionCount = {};
											return selectedRating?.activity_log?.map((log) => {
												const action = log.action;
												actionCount[action] = (actionCount[action] || 0) + 1;

												let label = "";
												if (action === "CREATE") {
													label = "Submit date";
												} else if (action === "UPDATE") {
													label = actionCount[action] === 1 ? "Update date" : "Again update date";
												}

												return (
													<div key={log.log_id} className="text-muted small mb-1">
														{label}: {fDateTime(log.createdAt)}
														{/* {log.changes && <div className="notification-details">{ObjectString({recode_data: log.changes, label, createdAt: fDateTime(log.createdAt)})}</div>} */}
													</div>
												);
											});
										})()}
									</div>
								</div>

								<div className="d-flex  align-items-center mb-2 mt-3 pt-3 border-top">
									<div className="text-muted small fw-semibold">
										Likes ({selectedRating?.likes?.length}) | Uplift ({selectedRating?.upliftList?.length})
									</div>
								</div>

								{selectedRating?.likes && selectedRating?.likes?.length > 0 && (
									<div className="">
										<div className="likes-list">
											{selectedRating?.likes.map((like, likeIndex) => (
												<div key={likeIndex} className="d-flex align-items-center py-1 px-2 mb-1 bg-light rounded">
													<div className="me-2">
														<Avatar variant="rounded" sx={{bgcolor: "#22c55e1a", color: "#28a745"}}>
															{/* <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
																<path
																	d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.5783 8.50903 2.9987 7.05 2.9987C5.59096 2.9987 4.19169 3.5783 3.16 4.61C2.1283 5.6417 1.5487 7.04097 1.5487 8.5C1.5487 9.95903 2.1283 11.3583 3.16 12.39L12 21.23L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22249 22.4518 8.5C22.4518 7.77751 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z"
																	stroke="currentColor"
																	strokeWidth="2"
																	strokeLinecap="round"
																	strokeLinejoin="round"
																/>
															</svg> */}
															<div className="bi-hand-thumbs-up" style={{fontSize: 20}} />
														</Avatar>
													</div>
													<div className="flex-grow-1">
														<div className="fw-semibold small">
															<CustomUserName user={like?.user} sx={{fontWeight: 600}} code />
														</div>
														<div className="text-muted" style={{fontSize: "0.65rem"}}>
															{formatDateTime(like?.createdAt)}
														</div>
													</div>
												</div>
											))}
										</div>
									</div>
								)}

								{selectedRating?.upliftList?.map((comments, likeIndex) => (
									<div key={likeIndex} className="d-flex align-items-center py-1 px-2 mb-1 bg-light rounded">
										<div className="me-2">
											<Avatar variant="rounded" sx={{bgcolor: "#ffc1071a", color: "#ffc107"}}>
												<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
													<path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
												</svg>
											</Avatar>
										</div>
										<div className="flex-grow-1">
											<div className="fw-semibold small">
												<CustomUserName user={comments?.user} sx={{fontWeight: 600}} code/>
											</div>

											<div className="text-muted" style={{fontSize: "0.65rem"}}>
												{formatDateTime(comments?.createdAt)}
											</div>

											{comments?.description}
										</div>
									</div>
								))}
							</div>
						</div>

						<div className="modal-footer">
							{selectedRating.canEdit && (
								<>
									<button
										className="btn btn-primary"
										onClick={() => {
											setShowDetailModal(false);
											handleEditRating(selectedRating.id);
										}}>
										Edit Rating
									</button>
									{/* <button
										className="btn btn-danger"
										onClick={() => {
											setShowDetailModal(false);
											handleDeleteRating(selectedRating.id);
										}}>
										Delete Rating
									</button> */}
								</>
							)}
							<button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{showEditModal && editingRating && (
				<div className="modal-overlay" onClick={closeEditModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: "800px"}}>
						<div className="modal-header">
							<h3>Edit Rating</h3>
							<button className="modal-close" onClick={closeEditModal}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="form-container">
								{/* Job Address */}
								<div className="form-group">
									<label className="form-label">Job Address *</label>
									<input
										type="text"
										className={`form-input ${editErrors.jobAddress ? "error" : ""}`}
										value={editFormData.jobAddress || ""}
										onChange={(e) => handleEditInputChange("jobAddress", e.target.value)}
										placeholder="Enter job site address"
										readOnly
									/>
									{editErrors.jobAddress && <div className="error-message">{editErrors.jobAddress}</div>}
								</div>

								{/* Job Issues */}
								<div className="form-group">
									<label className="form-label">Job Issues (Select all that apply)</label>
									<div className="job-issues-grid">
										{jobIssuesOptions.map((issue) => (
											<div key={issue.id} className="issue-checkbox" onClick={() => handleEditIssueToggle(issue.id)} style={{cursor: "pointer"}}>
												<input type="checkbox" id={`edit-issue-${issue.id}`} checked={editFormData.jobIssues?.includes(issue.id) || false} onChange={() => handleEditIssueToggle(issue.id)} />
												<div className="checkbox-custom"></div>
												<label htmlFor={`edit-issue-${issue.id}`} className="checkbox-label">
													{issue.label}
												</label>
											</div>
										))}
									</div>
								</div>

								{/* Job Rating */}
								<div className="form-group">
									<label className="form-label">Job Rating *</label>
									<div className="rating-container">
										<div className="star-rating-large">
											{[1, 2, 3, 4, 5].map((star) => (
												<button key={star} type="button" className={`star-btn-large ${star <= (editFormData.jobRating || 0) ? "filled" : ""}`} onClick={() => handleEditRatingChange(star)}>
													★
												</button>
											))}
										</div>
										<div className="rating-text">
											{editFormData.jobRating ? (
												<span className="rating-description">
													{editFormData.jobRating === 5 ? "Excellent" : editFormData.jobRating === 4 ? "Good" : editFormData.jobRating === 3 ? "Fair" : editFormData.jobRating === 2 ? "Poor" : "Very Poor"}
												</span>
											) : (
												<span className="rating-description">Select a rating</span>
											)}
										</div>
									</div>
									{editErrors.jobRating && <div className="error-message">{editErrors.jobRating}</div>}
								</div>

								<div className="form-group">
									<label className="form-label">Job Date *</label>
									<input
										type="date"
										onClick={(e) => {
											e.target.showPicker && e.target.showPicker();
										}}
										className={`form-input ${editErrors.jobDate ? "error" : ""}`}
										value={editFormData.jobDate || ""}
										onChange={(e) => handleEditInputChange("jobDate", e.target.value)}
										max={new Date().toISOString().split("T")[0]}
									/>
									{editFormData.jobDate && (
										<div className={`date-status ${calculateJobDateStatus(editFormData.jobDate).type}`}>
											<span className="status-icon">{calculateJobDateStatus(editFormData.jobDate).type === "fresh" ? "🟢" : "🟡"}</span>
											<span className={`status-${calculateJobDateStatus(editFormData.jobDate).type}`}>{calculateJobDateStatus(editFormData.jobDate).label}</span>
											<small>{calculateJobDateStatus(editFormData.jobDate).description}</small>
										</div>
									)}
									{editErrors.jobDate && <div className="error-message">{editErrors.jobDate}</div>}
								</div>

								{editingRating && (
									<div className="form-group">
										<label className="form-label">Changes Summary</label>
										<div className="changes-preview">
											{getChanges().length > 0 ? (
												<div className="changes-list">
													{getChanges().map((change, index) => (
														<div key={index} className="change-item">
															<strong>{change.field}:</strong>
															<span className="change-from">"{change.from}"</span>
															<span className="change-arrow">→</span>
															<span className="change-to">"{change.to}"</span>
														</div>
													))}
												</div>
											) : (
												<p className="no-changes">No changes detected</p>
											)}
										</div>
									</div>
								)}

								{editingRating?.changes && editingRating.changes.length > 0 && (
									<div className="form-group">
										<label className="form-label">Edit History</label>
										<div className="edit-history">
											{editingRating.changes.map((edit, index) => (
												<div key={index} className="edit-entry">
													<div className="edit-header">
														<strong>Edit #{edit.editNumber}</strong>
														<span className="edit-date">
															{moment(edit.date).format("MMM DD, YYYY")} at {moment(edit.date).format("MMM DD, YYYY")}
														</span>
													</div>
													<div className="edit-changes">
														{edit.changes.map((change, changeIndex) => (
															<div key={changeIndex} className="edit-change">
																{change.field}: "{change.from}" → "{change.to}"
															</div>
														))}
													</div>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						</div>

						<div className="modal-footer">
							<button className="btn btn-primary" onClick={handleEditSubmit} disabled={isSubmittingEdit || getChanges().length === 0}>
								{isSubmittingEdit ? (
									<>
										<span className="spinner-small"></span>
										Saving Changes...
									</>
								) : (
									"Save Changes"
								)}
							</button>
							<button className="btn btn-secondary" onClick={closeEditModal} disabled={isSubmittingEdit}>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default MyRatings;
